<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { CreditSearchInstitution } from '@/models/credit-search/api'

defineOptions({ name: 'CreditSearchInstitutionAutocomplete' })

const props = defineProps<{
  modelValue: CreditSearchInstitution | null
  institutions: CreditSearchInstitution[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: CreditSearchInstitution | null]
}>()

const input = ref<HTMLInputElement | null>(null)
const inputValue = ref('')
const menuOpen = ref(false)
const highlightedIndex = ref(-1)

const selectedLabel = computed(() => {
  if (!props.modelValue) return ''
  return `${props.modelValue.name} - ${props.modelValue.country.name}`
})

const filteredInstitutions = computed(() => {
  const search = inputValue.value.trim().toLowerCase()
  if (search.length < 2) return []
  return props.institutions.filter((institution) => {
    const label = `${institution.name} - ${institution.country.name}`.toLowerCase()
    return label.includes(search)
  })
})

watch(selectedLabel, (label) => {
  if (document.activeElement !== input.value) inputValue.value = label
}, { immediate: true })

function updateMenu(): void {
  menuOpen.value = filteredInstitutions.value.length > 0
  highlightedIndex.value = -1
}

function onInput(): void {
  if (inputValue.value === '') emit('update:modelValue', null)
  updateMenu()
}

function onFocus(): void {
  input.value?.select()
  updateMenu()
}

function onBlur(): void {
  window.setTimeout(() => {
    menuOpen.value = false
    if (inputValue.value === '') {
      emit('update:modelValue', null)
      return
    }
    inputValue.value = selectedLabel.value
  }, 10)
}

function selectInstitution(institution: CreditSearchInstitution): void {
  emit('update:modelValue', institution)
  inputValue.value = `${institution.name} - ${institution.country.name}`
  menuOpen.value = false
  highlightedIndex.value = -1
  nextTick(() => input.value?.blur())
}

function onKeydown(event: KeyboardEvent): void {
  if (!menuOpen.value || filteredInstitutions.value.length === 0) return
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    highlightedIndex.value = (highlightedIndex.value + 1) % filteredInstitutions.value.length
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlightedIndex.value = highlightedIndex.value <= 0
      ? filteredInstitutions.value.length - 1
      : highlightedIndex.value - 1
  } else if (event.key === 'Enter' && highlightedIndex.value >= 0) {
    event.preventDefault()
    const institution = filteredInstitutions.value[highlightedIndex.value]
    if (institution) selectInstitution(institution)
  } else if (event.key === 'Escape') {
    menuOpen.value = false
  }
}
</script>

<template>
  <div class="institution-autocomplete">
    <input
      id="institutionSeachBox"
      ref="input"
      v-model="inputValue"
      class="form-control"
      placeholder="Enter an institution here"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="menuOpen"
      aria-controls="institution-autocomplete-list"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    >
    <ul
      v-if="menuOpen"
      id="institution-autocomplete-list"
      class="ui-autocomplete ui-menu"
      role="listbox"
    >
      <li
        v-for="(institution, index) in filteredInstitutions"
        :key="institution.id"
        class="ui-menu-item"
        :class="{ 'ui-state-active': index === highlightedIndex }"
        role="option"
        :aria-selected="index === highlightedIndex"
        @mousedown.prevent="selectInstitution(institution)"
      >
        {{ institution.name }} - {{ institution.country.name }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.institution-autocomplete {
  position: relative;

  .ui-autocomplete {
    position: absolute;
    z-index: 10;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    background: #fff;
    border: 1px solid #808080;
    box-shadow: 5px 0 8px rgba(255, 255, 255, 0.9);
    line-height: 2;

    .ui-menu-item {
      padding: 0 0.5rem;
      cursor: pointer;

      &.ui-state-active,
      &:hover {
        background: #d3d3d3;
      }
    }
  }
}
</style>
